// 引入 className 合并工具。
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
// 引入时间格式化库。
import dayjs from "dayjs";
// 引入通用 Head 组件。
import CommonHead from "@/components/CommonHead";
// 引入通用顶部导航组件。
import CommonHeader from "@/components/CommonHeader";
// 引入通用图片组件。
import CommonImage, { ImageType } from "@/components/CommonImage";
// 引入通用图标组件。
import CommonIcon from "@/components/CommonIcon";
// 引入自定义 Tooltip 组件。
import CustomTooltip from "@/components/CustomTooltip";
// 引入侧栏组件类型。
import { SidebarList, HomeSidebarListOption } from "@/components/HomeSidebar/SideItems";
// 引入侧栏底部组件。
import SideBarFooter from "@/components/HomeSidebar/SideBarFooter";
// 引入设备弹窗。
import MyDevicesModal from "../components/MyDevicesModal";
// 引入积分兑换弹窗。
import PointsExchangeModal from "../components/PointsExchangeModal";
// 引入编辑资料弹窗。
import EditProfileModal from "../components/EditProfileModal";
import AdminPrivacyInfoModal from "../components/AdminPrivacyInfoModal";
import VipIcon from "../../../assets/icon/account/vip.svg";
import InfoIcon from "../../../assets/icon/account/info.svg";
import PreviewCloseIcon from "../../../assets/icon/account/pc-preview-close.svg";
import PreviewOpenIcon from "../../../assets/icon/account/pc-preview-open.svg";
import GoToInfoIcon from "../../../assets/icon/account/go-to-info-pc.svg";
import AddPostIcon from "../../../assets/icon/homeSidebar/add-post.svg";
import { IUserEquipment } from "@/api/device";
import { IUserLoginInfo, IUserInfo } from "@/api/user";
import { ITopLevelNav } from "@/api/nav";
import styles from "../index.module.scss";

// PC 布局组件入参类型。
interface Props {
  t: (key: string, values?: any) => string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  account: IUserLoginInfo;
  navList: ITopLevelNav[];
  saltkey: string;
  isSelfView: boolean;
  isAdminView: boolean;
  displayAccount: any;
  displayName: string;
  shouldShowAdminNameTooltip: boolean;
  badgeIconError: boolean;
  setBadgeIconError: (val: boolean) => void;
  handleEditProfileClick: () => void;
  showFlightInfo: boolean;
  displayFlightDistance: string;
  hideDistance: boolean;
  handleToggleFlightVisibility: () => void;
  handlePointsExchangeClick: () => void;
  displayPoints: string | number;
  deviceCount: number;
  previewDevices: IUserEquipment[];
  showMoreDevices: boolean;
  handleDeviceIconClick: (deviceLink?: string, productName?: string) => void;
  setShowDevicesModal: (visible: boolean) => void;
  adminUid: any;
  adminEmail: any;
  adminIp: any;
  adminLocation: any;
  adminLastLoginTime: any;
  sidebarOptions: HomeSidebarListOption[];
  activeTab: string;
  handlePost: () => void;
  visibleTabKeys: string[];
  renderTabPanel: (tabKey: string) => React.ReactNode;
  transparentMain: boolean;
  showDevicesModal: boolean;
  initialDevices: IUserEquipment[];
  showPointsExchangeModal: boolean;
  setShowPointsExchangeModal: (v: boolean) => void;
  setPoints: (v: number) => void;
  showEditProfileModal: boolean;
  setShowEditProfileModal: (v: boolean) => void;
  initialUserInfo: IUserInfo | null | undefined;
  profilePatch: { name?: string; avatarUrl?: string } | null;
  refreshGlobalAccount: () => void;
  setProfilePatch: (payload: any) => void;
  onCreditsChange: (newCredits: number) => void;
}

/**
 * 用户中心 PC 页面布局组件。
 *
 * 滚动架构：采用纯原生单滚动条方案。
 * - 页面使用浏览器原生滚动（#__next 或 body），不做任何 JS 滚动劫持。
 * - mainContent 不设高度限制和 overflow，内容有多长就多长，自然撑开文档高度。
 * - Sidebar 使用 CSS sticky 定位（top: 80px），跟随全局滚动自然吸顶。
 * - 灰色遮挡条（stickyCoverBar）使用 CSS sticky 定位（top: 64px），吸顶后遮住
 *   列表顶部边缘，配合 margin-top: -16px 与 Banner 的 padding-bottom 重叠，
 *   视觉上模拟内容区吸顶效果。
 * - 全局滚动条 1:1 反映所有内容的滚动位置，零 JS 参与滚动过程。
 */
export default function UserCenterPcLayout(props: Props) {
  const {
    t,
    seoTitle,
    seoDescription,
    seoKeywords,
    account,
    navList,
    saltkey,
    isSelfView,
    isAdminView,
    displayAccount,
    displayName,
    shouldShowAdminNameTooltip,
    badgeIconError,
    setBadgeIconError,
    handleEditProfileClick,
    showFlightInfo,
    displayFlightDistance,
    hideDistance,
    handleToggleFlightVisibility,
    handlePointsExchangeClick,
    displayPoints,
    deviceCount,
    previewDevices,
    showMoreDevices,
    handleDeviceIconClick,
    setShowDevicesModal,
    adminUid,
    adminEmail,
    adminIp,
    adminLocation,
    adminLastLoginTime,
    sidebarOptions,
    activeTab,
    handlePost,
    visibleTabKeys,
    renderTabPanel,
    transparentMain,
    showDevicesModal,
    initialDevices,
    showPointsExchangeModal,
    setShowPointsExchangeModal,
    setPoints,
    showEditProfileModal,
    setShowEditProfileModal,
    initialUserInfo,
    profilePatch,
    refreshGlobalAccount,
    setProfilePatch,
    onCreditsChange,
  } = props;
  const [isCompactSidebar, setIsCompactSidebar] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncCompactSidebar = () => {
      const width = window.innerWidth;
      setIsCompactSidebar(width < 1092);
    };

    syncCompactSidebar();
    window.addEventListener("resize", syncCompactSidebar);

    return () => {
      window.removeEventListener("resize", syncCompactSidebar);
    };
  }, []);

  const deviceModalThreshold = isCompactSidebar ? 3 : 4;
  const canOpenDevicesModal = deviceCount > deviceModalThreshold;
  const visibleDevicePreview = useMemo(() => {
    if (!Array.isArray(previewDevices)) return [];
    if (!isCompactSidebar) return previewDevices;
    return canOpenDevicesModal ? previewDevices.slice(0, 2) : previewDevices.slice(0, 3);
  }, [canOpenDevicesModal, isCompactSidebar, previewDevices]);
  const visibleDevicePlaceholderCount = useMemo(() => {
    if (!isCompactSidebar) {
      return Math.min(4, deviceCount || 0);
    }
    return canOpenDevicesModal ? 2 : Math.min(3, deviceCount || 0);
  }, [canOpenDevicesModal, deviceCount, isCompactSidebar]);
  const [showAdminPrivacyModal, setShowAdminPrivacyModal] = useState(false);

  const formatAdminValue = (val: any) => (val ? val : "--");
  // 管理员最后登录时间格式化。
  const formatAdminTime = (val: any) => {
    if (!val) return "--";
    return dayjs.unix(Number(val)).format("YYYY-MM-DD");
  };

  // 渲染页面结构：head/header/banner/coverBar/sidebar/content/modals。
  return (
    <div className={styles.root}>
      <CommonHead title={seoTitle} description={seoDescription} keywords={seoKeywords} />
      <div className={styles.header}>
        <CommonHeader
          hasNav={true}
          account={account}
          navsList={navList || []}
          saltkey={saltkey}
          onCreditsChange={onCreditsChange}
        />
      </div>
      <div className={styles.mainContainer}>
        {/* 用户信息 Banner 区 */}
        <section className={styles.userInfoBannerContainer}>
          <div className={styles.userInfoBanner}>
            <div className={styles.userProfile}>
              <CommonImage
                className={styles.avatar}
                src={displayAccount?.avatar?.large || ""}
                type={ImageType.avatar}
                layout="fill"
                cutMode="cover"
                alt={displayAccount?.name}
              />
              <div className={styles.info}>
                <div className={styles.nameRow}>
                  {shouldShowAdminNameTooltip ? (
                    <CustomTooltip title={displayName} placement="top">
                      <h1 className={cx(styles.name, { [styles.nameAdmin]: isAdminView })}>{displayName}</h1>
                    </CustomTooltip>
                  ) : (
                    <h1 className={cx(styles.name, { [styles.nameAdmin]: isAdminView })}>{displayName}</h1>
                  )}
                  {isSelfView && (
                    <div className={styles.editButton} onClick={handleEditProfileClick}>
                      <CommonIcon icon="account-edit" className={styles.editIcon} />
                    </div>
                  )}
                  {isAdminView && (
                    <CustomTooltip title={t("user_center_pc_view_user_info")} placement="top">
                      <div
                        className={styles.infoButton}
                        role="button"
                        tabIndex={0}
                        aria-label={t("user_center_pc_view_user_info")}
                        onClick={() => setShowAdminPrivacyModal(true)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setShowAdminPrivacyModal(true);
                          }
                        }}
                      >
                        <InfoIcon className={styles.infoIcon} />
                      </div>
                    </CustomTooltip>
                  )}
                </div>
                <div className={styles.tagsRow}>
                  {displayAccount?.rank?.vname && (
                    <div className={cx(styles.badge, styles.verifyBadge)}>
                      <VipIcon className={styles.verifyIcon} />
                      <span className={styles.badgeText}>{displayAccount.rank.vname}</span>
                    </div>
                  )}
                  {displayAccount?.group && (
                    <div className={styles.badge}>
                      {!badgeIconError && displayAccount.group.icon && (
                        <CommonImage
                          className={styles.badgeIcon}
                          src={displayAccount.group.icon}
                          type={ImageType.icon}
                          layout={[20, 20]}
                          cutMode="contain"
                          alt={displayAccount.group.title}
                          onError={() => setBadgeIconError(true)}
                        />
                      )}
                      <span className={styles.badgeText}>{displayAccount.group.title}</span>
                    </div>
                  )}
                  {showFlightInfo && (
                    <div className={styles.flightInfo}>
                      <CommonIcon icon="account-send" className={styles.planeIcon} />
                      <span className={styles.flightText}>{t("user_profile_flight_distance")}</span>
                      <span className={styles.flightDistance}>{displayFlightDistance}</span>
                      {isSelfView && (
                        <CustomTooltip
                          title={hideDistance ? t("user_center_pc_flight_private_tip") : t("user_center_pc_flight_public_tip")}
                          placement="top"
                          className={styles.eyeIconWrapper}
                        >
                          <div className={styles.eyeIcon} role="button" tabIndex={0} aria-label={t("user_center_pc_toggle_visibility")} onClick={handleToggleFlightVisibility}>
                            {hideDistance ? <PreviewCloseIcon /> : <PreviewOpenIcon />}
                          </div>
                        </CustomTooltip>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.userStats}>
              <div className={styles.statCard}>
                <div className={styles.cardHeader}>
                  <span>{isSelfView ? t("user_center_pc_my_points") : t("user_center_pc_ta_points")}</span>
                  {isSelfView && (
                    <span className={styles.arrow} role="button" tabIndex={0} onClick={handlePointsExchangeClick} style={{ cursor: "pointer" }}>
                      {t("user_profile_action_exchange")} <GoToInfoIcon className={styles.icon} />
                    </span>
                  )}
                </div>
                <div className={styles.cardValue}>{displayPoints}</div>
              </div>
              <div className={cx(styles.statCard, styles.deviceStatCard, { [styles.clickable]: canOpenDevicesModal })} onClick={() => canOpenDevicesModal && setShowDevicesModal(true)}>
                <div className={styles.cardHeader}>
                  <span>{isSelfView ? t("user_center_pc_my_devices") : t("user_center_pc_ta_devices")}</span>
                  <span className={cx(styles.arrow, { [styles.arrowCompactOverflow]: isCompactSidebar && deviceCount > 9 })}>
                    {typeof deviceCount === "number" ? t("user_profile_devices_count", { count: deviceCount }) : t("user_profile_devices_count", { count: 0 })}
                    {canOpenDevicesModal && <GoToInfoIcon className={styles.icon} />}
                  </span>
                </div>
                <div className={styles.deviceList}>
                  {typeof deviceCount === "number" && deviceCount <= 0 ? (
                    <div className={cx(styles.deviceIcon, styles.noDevice)}>
                      <CommonIcon icon="empty-device" />
                    </div>
                  ) : (
                    <>
                      {visibleDevicePreview.length > 0
                        ? visibleDevicePreview.map((d: IUserEquipment, idx: number) => {
                            const deviceLink = d?.home_page;
                            return (
                              <CustomTooltip
                                key={deviceLink || d?.name || d?.icon || idx}
                                title={d?.name || ""}
                                placement="top"
                              >
                                <div
                                  className={styles.deviceIcon}
                                  role={deviceLink ? "button" : undefined}
                                  tabIndex={deviceLink ? 0 : undefined}
                                  aria-label={deviceLink ? t("user_center_pc_view_device", { name: d?.name || "" }) : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeviceIconClick(deviceLink, d?.name);
                                  }}
                                  onKeyDown={(e) => {
                                    if (!deviceLink) return;
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeviceIconClick(deviceLink, d?.name);
                                    }
                                  }}
                                >
                                  <CommonImage src={d?.icon || ""} type={ImageType.device} layout={[28, 28]} cutMode="contain" alt={d?.name} />
                                </div>
                              </CustomTooltip>
                            );
                          })
                        : Array.from({ length: visibleDevicePlaceholderCount }, (_, idx) => (
                            <div key={idx} className={styles.deviceIcon}>
                              <CommonImage src="" type={ImageType.device} layout={[28, 28]} cutMode="contain" alt="device" />
                            </div>
                          ))}
                      {(isCompactSidebar ? canOpenDevicesModal : showMoreDevices) && (
                        <div className={styles.deviceIcon}>
                          <CommonIcon icon="my-device-more" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/*
          吸顶遮挡条：
          - 初始位置在 Banner 下方，与 Banner 的 padding-bottom(16px) 通过 margin-top:-16px 重叠，不占额外空间。
          - 随页面滚动到 top:64px（Header 底部）时 sticky 吸住。
          - 吸住后遮住列表内容的顶部边缘，视觉上模拟"内容区从 Header 下方开始"的吸顶效果。
          - 背景色与页面背景 #F2F5F7 一致，pointer-events:none 不影响交互。
        */}
        <div className={styles.stickyCoverBar} aria-hidden="true" />
        <div className={styles.contentBody}>
          {/* 本人视角左侧导航区 */}
          {isSelfView && (
            <aside className={cx(styles.sidebar, { [styles.sidebarCompact]: isCompactSidebar })}>
              <nav>
                <SidebarList options={sidebarOptions} activeKey={activeTab} disableResponsive={!isCompactSidebar} compact={isCompactSidebar} />
              </nav>
              <div className={cx(styles.actionArea, { [styles.actionAreaCompact]: isCompactSidebar })}>
                <button className={cx(styles.postButton, { [styles.postButtonCompact]: isCompactSidebar })} onClick={handlePost} aria-label={t("user_center_pc_post")}>
                  <span className={styles.icon}>
                    <AddPostIcon />
                  </span>
                  {!isCompactSidebar && t("user_center_pc_post")}
                </button>
              </div>
              <div className={cx(styles.sidebarFooter, { [styles.sidebarFooterCompact]: isCompactSidebar })}>
                <SideBarFooter disableResponsive={!isCompactSidebar} compact={isCompactSidebar} />
              </div>
            </aside>
          )}
          {/* 右侧主内容区：无高度限制、无 overflow，内容自然撑开，由全局滚动条统一控制滚动 */}
          <div className={styles.mainContentColumn}>
            <main
              id="user-center-main-content"
              className={cx(styles.mainContent, {
                [styles.transparentBg]: transparentMain,
              })}
            >
            {/* Tab 面板通过 display:block/none 切换，同一时刻只有一个 Tab 可见。
                所有 Tab 的 DOM 始终存在，切换时不卸载/重建，保留各 Tab 的列表数据和内部状态。 */}
            {visibleTabKeys.map((tabKey) => (
                <div key={tabKey} className={cx(styles.tabPanel, { [styles.tabPanelActive]: activeTab === tabKey, [styles.tabPanelHidden]: activeTab !== tabKey })}>
                  {renderTabPanel(tabKey)}
                </div>
              ))}
            </main>
          </div>
        </div>
      </div>
      {/* 设备弹窗 */}
      <MyDevicesModal visible={showDevicesModal} onClose={() => setShowDevicesModal(false)} devices={initialDevices} isSelfView={isSelfView} onDeviceClick={handleDeviceIconClick} />
      {/* 积分兑换弹窗 */}
      <PointsExchangeModal visible={showPointsExchangeModal} onClose={() => setShowPointsExchangeModal(false)} onPointsUpdate={setPoints} />
      {/* 编辑资料弹窗 */}
      <EditProfileModal
        visible={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        userInfo={
          initialUserInfo
            ? {
                ...initialUserInfo,
                name: profilePatch?.name || initialUserInfo.name,
                avatar: profilePatch?.avatarUrl || initialUserInfo.avatar,
              }
            : null
        }
        account={account}
        onSuccess={(payload) => {
          if (payload?.name || payload?.avatarUrl) {
            setProfilePatch((prev: any) => ({
              ...prev,
              ...payload,
            }));
          }
          refreshGlobalAccount();
        }}
      />
      <AdminPrivacyInfoModal
        visible={showAdminPrivacyModal}
        onClose={() => setShowAdminPrivacyModal(false)}
        uid={formatAdminValue(adminUid)}
        email={formatAdminValue(adminEmail)}
        ip={formatAdminValue(adminIp)}
        location={formatAdminValue(adminLocation)}
        lastLoginTime={formatAdminTime(adminLastLoginTime)}
      />
    </div>
  );
}
